import { useEffect } from "react";
import { setTextVariableCustomFields } from "@/components/editor/textVariables";
import { setCMSRowCache } from "@/components/editor/resolveTextVariables";

/** Base field names for each auto-generated table (mirrored from cmsService). */
const HOTSPOTS_BASE_FIELDS = new Set([
  "id", "name", "type", "panorama_id", "panorama_name", "panorama_file",
  "hotspot_style", "visible", "order",
  "position_x", "position_y", "position_z",
  "rotation_x", "rotation_y", "rotation_z",
  "scale_x", "scale_y", "scale_z",
  "content", "cms_action", "created_at", "updated_at",
]);

const PANORAMAS_BASE_FIELDS = new Set([
  "id", "name", "panorama_file",
  "file_url", "file_url_webp", "thumbnail_url", "thumbnail_url_webp",
  "width", "height", "file_size", "order",
  "file_status", "processing_status", "private_file_url",
  "coordinates", "created_at",
]);

type FieldStructure = { name: string; displayName?: string };
type CMSDataRecord = {
  name: string;
  fieldStructure: unknown;
  data?: {
    fields?: FieldStructure[];
    rows?: Array<Record<string, unknown>>;
  };
};

/**
 * Loads CMS field definitions and row data for the current project.
 * - Registers custom fields so they appear in text-variable autocomplete.
 * - Caches CMS row data so the resolver can look up custom field values.
 */
export function useTextVariableFieldSync(
  projectId: string | undefined | null,
  craftspaceId?: string | null,
) {
  useEffect(() => {
    if (!projectId || !craftspaceId) return;

    let cancelled = false;

    const sync = async () => {
      try {
        const { CMSService } = await import("@/services/cmsService");
        const records: CMSDataRecord[] = await CMSService.loadProjectCMSData(projectId);

        if (cancelled) return;

        const custom: Record<string, { name: string; description: string }[]> = {};
        const hotspotRows = new Map<string, Record<string, unknown>>();
        const panoramaRows = new Map<string, Record<string, unknown>>();

        for (const record of records) {
          const tableName = record.name.toLowerCase();
          const isHotspots = tableName === "hotspots" || tableName === "hotspots collection";
          const isPanoramas = tableName === "panoramas" || tableName === "panoramas collection";

          if (!isHotspots && !isPanoramas) continue;

          // Load full dataset (fields + rows) from IndexedDB for accurate custom fields + values
          let dataset: CMSDataRecord | null = null;
          try {
            dataset = await CMSService.loadCMSDataWithContent(record.id, craftspaceId);
          } catch {
            dataset = null;
          }

          const fields: FieldStructure[] =
            dataset?.data?.fields ??
            record.data?.fields ??
            ((record.fieldStructure ?? []) as FieldStructure[]);

          // Custom fields for autocomplete
          const baseFields = isHotspots ? HOTSPOTS_BASE_FIELDS : PANORAMAS_BASE_FIELDS;
          const key = isHotspots ? "hotspots" : "panoramas";

          const customFields = fields
            .filter((f) => !baseFields.has(f.name))
            .map((f) => ({
              name: f.name,
              description: f.displayName ?? f.name,
            }));

          if (customFields.length > 0) {
            custom[key] = [...(custom[key] ?? []), ...customFields];
          }

          // Cache row data for resolver (index by id)
          const rows = dataset?.data?.rows ?? record.data?.rows ?? [];
          for (const row of rows) {
            const id = typeof row.id === "string" ? row.id : String(row.id ?? "");
            if (!id) continue;
            if (isHotspots) {
              hotspotRows.set(id, row);
            } else {
              panoramaRows.set(id, row);
            }
          }
        }

        setTextVariableCustomFields(custom);
        setCMSRowCache(hotspotRows, panoramaRows);
      } catch {
        // Silently ignore – custom fields just won't appear
      }
    };

    sync();
    return () => { cancelled = true; };
  }, [projectId, craftspaceId]);
}
